import React, {
    useReducer,
    useEffect,
    useContext,
    useRef,
    useMemo,
    useCallback,
    useState,
} from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Avatar,
    useTheme,
    useMediaQuery,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@material-ui/core';
import throttle from 'lodash/throttle';
import PlayCircleIcon from '@material-ui/icons/PlayCircleOutlineOutlined';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import LocalCafeIcon from '@material-ui/icons/LocalCafe';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import SettingsIcon from '@material-ui/icons/Settings';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import PrintIcon from '@material-ui/icons/Print';
import SearchIcon from '@material-ui/icons/Search';
import { useApolloClient, useQuery, useMutation } from '@apollo/client';
import moment from 'moment-timezone';
import printJS from 'print-js';
import { ALL_LOTS, FetchPolicy } from 'utils/enum/Core';
import DateUtils from 'lib/DateUtils';
import KeyStore from 'utils/KeyStore';
import PayrollGeneralQuery from 'services/graphQL/query/payroll/PayrollGeneral';
import PayrollGeneralMutation from 'services/graphQL/mutate/payroll/PayrollGeneral';
import HoursWorkedMutation from 'services/graphQL/mutate/payroll/HoursWorked';
import UserContext from 'components/context/UserContext';
import useLogOut from 'components/hook/core/useLogOut';
import ModalUtils from 'utils/ModalUtils';
import MessageUtils from 'utils/MessageUtils';
import { useTimeClockHomeStyles } from 'styles/modules/timeclock/TimeClockStyles';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';

import PropTypes from 'prop-types';

const ONE_SECOND_IN_MS = 1000;

const ClockDisplay = ({ userTimeZone, timeFormat }) => {
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const updateInterval = ONE_SECOND_IN_MS;
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, updateInterval);

        return () => clearInterval(timer);
    }, []);

    return (
        <>
            {DateUtils.getDateByTimezone(currentTime, userTimeZone).format(timeFormat)}
        </>
    );
};

ClockDisplay.propTypes = {
    userTimeZone: PropTypes.string.isRequired,
    timeFormat: PropTypes.string.isRequired,
};

const TIME_CLOCK_ACTION_REASON = Object.freeze({
    START_WORK: 'Start Work',
    STOP_FOR_LUNCH_BREAK: 'Lunch',
    STOP_FOR_SHORT_BREAK: 'Break',
    STOP_FOR_MEDICAL: 'Doctor',
    LEAVE_FOR_THE_DAY: 'End Day',
    STOP_FOR_VACATION: 'Vacation',
    STOP_FOR_OTHER: 'Other',
});

const ACTION_TILES = [
    { icon: RestaurantIcon, label: 'Leave for Lunch', reason: TIME_CLOCK_ACTION_REASON.STOP_FOR_LUNCH_BREAK },
    { icon: LocalCafeIcon, label: 'Leave for Break', reason: TIME_CLOCK_ACTION_REASON.STOP_FOR_SHORT_BREAK },
    { icon: LocalHospitalIcon, label: 'Left to Doctor', reason: TIME_CLOCK_ACTION_REASON.STOP_FOR_MEDICAL },
    { icon: FlightTakeoffIcon, label: 'On Vacation', reason: TIME_CLOCK_ACTION_REASON.STOP_FOR_VACATION },
    { icon: SettingsIcon, label: 'Other', reason: TIME_CLOCK_ACTION_REASON.STOP_FOR_OTHER },
];

const ACTION_TILE_COLOR_CLASS_BY_REASON = Object.freeze({
    [TIME_CLOCK_ACTION_REASON.STOP_FOR_LUNCH_BREAK]: 'actionButtonLunch',
    [TIME_CLOCK_ACTION_REASON.STOP_FOR_SHORT_BREAK]: 'actionButtonBreak',
    [TIME_CLOCK_ACTION_REASON.STOP_FOR_MEDICAL]: 'actionButtonMedical',
    [TIME_CLOCK_ACTION_REASON.STOP_FOR_VACATION]: 'actionButtonVacation',
    [TIME_CLOCK_ACTION_REASON.STOP_FOR_OTHER]: 'actionButtonOther',
});

const UI_TEXT = Object.freeze({
    SHIFT_ACTIVE: 'Shift Active',
    CURRENTLY_OUT: 'Currently Out',
    LEAVE_FOR_THE_DAY: 'LEAVE FOR THE DAY',
    START_WORK: 'START WORK',
    RETURN_DIALOG_TITLE: 'Please provide your estimated time of return?',
    SELECT_RETURN_TIME: 'Select return time:',
    SELECT_DATE_TIME: 'Select date and time:',
    SELECT_REASON: 'Select reason:',
    CANCEL: 'Cancel',
    CONFIRM: 'Confirm',
    SEARCH_PLACEHOLDER: 'Search by first or last name',
    GENERATE_REPORT: 'GENERATE REPORT',
    LOADING_DAILY_LOG: 'Loading daily action log...',
    NO_DAILY_ACTIONS: 'No daily actions found.',
    LOADING_MORE_ACTIONS: 'Loading more actions...',
    RETURN_TIME_REQUIRED: 'Please provide an estimated time of return.',
    REASON_REQUIRED: 'Please provide a reason.',
    LOGOUT_CONFIRM_TITLE: 'Confirm Logout',
    LOGOUT_CONFIRM_DESCRIPTION: 'Do you want to log out of the system?',
    YES: 'Yes',
    NO: 'No',
});

const TIME_FORMAT = Object.freeze({
    MOBILE: 'hh:mm A',
    DESKTOP: 'hh:mm:ss A',
});

const DAILY_LOG_TIME_FORMAT = 'hh:mm A';
const DAILY_LOG_DATE_TIME_FORMAT = 'MM/DD/YYYY hh:mm A';

const INPUT_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm';
const BREAK_RETURN_TIME_OPTIONS = [
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
];

const VACATION_REASON_OPTIONS = [
    'Vacation without pay',
    'Vacation with pay',
    'Time off without pay',
    'Time off with pay',
    'Holiday',
    'Holiday with pay',
    'Sick leave without pay',
    'Sick leave with pay',
    'FMLA',
];

const DAILY_LOG_PAGE_SIZE = 25;
const DAILY_LOG_SCROLL_THRESHOLD = 80;

const INITIAL_ACTION_DIALOG_STATE = Object.freeze({
    actionReturnDialogReason: '',
    selectedReturnTime: '30',
    medicalReturnDateTime: '',
    otherManualReason: '',
    selectedVacationReason: VACATION_REASON_OPTIONS[0],
    isLogoutConfirmDialogOpen: false,
});

const getInitialUserTimeZone = () => {
    let timeZone = moment.tz.guess();
    const userLot = new KeyStore().getSelectedLot();

    if (userLot?.isDefault && userLot?.lotTimeZoneId) {
        timeZone = userLot.lotTimeZoneId;
    }

    return timeZone;
};

const createInitialState = (currentYear) => ({
    selectedPeriod: '',
    selectedYear: currentYear,
    logSearchTerm: '',
    appliedLogSearchTerm: '',
    dailyActionLogRows: [],
    isDailyActionLogLoading: false,
    hasMoreDailyLogs: true,
    isFetchingMoreDailyLogs: false,
    ...INITIAL_ACTION_DIALOG_STATE,
    userTimeZone: getInitialUserTimeZone(),
});

const timeClockReducer = (state, action) => {
    switch (action.type) {
    case 'PATCH_STATE': {
        const hasChanges = Object.keys(action.payload).some((key) => state[key] !== action.payload[key]);
        if (!hasChanges) {
            return state;
        }
        return {
            ...state,
            ...action.payload,
        };
    }
    case 'RESET_ACTION_DIALOG': {
        const hasChanges = Object.keys(INITIAL_ACTION_DIALOG_STATE).some((key) => state[key] !== INITIAL_ACTION_DIALOG_STATE[key]);
        if (!hasChanges) {
            return state;
        }
        return {
            ...state,
            ...INITIAL_ACTION_DIALOG_STATE,
        };
    }
    default:
        return state;
    }
};

const TimeClockHome = () => {
    const classes = useTimeClockHomeStyles();
    const apolloClient = useApolloClient();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { userInformation } = useContext(UserContext);
    const { logOut, executingLogOut } = useLogOut();
    const isSupervisor = userInformation?.isSupervisor || false;
    const selectedLotName = useMemo(() => new KeyStore().getSelectedLot()?.lotName || ALL_LOTS, []);
    const currentYear = new Date().getFullYear();
    const [state, dispatch] = useReducer(timeClockReducer, currentYear, createInitialState);
    const {
        selectedPeriod,
        selectedYear,
        logSearchTerm,
        appliedLogSearchTerm,
        dailyActionLogRows,
        isDailyActionLogLoading,
        hasMoreDailyLogs,
        isFetchingMoreDailyLogs,
        actionReturnDialogReason,
        selectedReturnTime,
        medicalReturnDateTime,
        otherManualReason,
        selectedVacationReason,
        isLogoutConfirmDialogOpen,
        userTimeZone,
    } = state;
    const appliedLogSearchTermRef = useRef('');
    const isFetchingMoreDailyLogsRef = useRef(false);

    const showTimeClockError = (action, entity) => {
        ModalUtils.errorMessage(null, MessageUtils.getGenericError(action, entity));
    };

    const { data: payPeriodsData, refetch, loading: isLoadingPayPeriods } = useQuery(PayrollGeneralQuery.GET_PAY_PERIODS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        variables: {
            lotName: selectedLotName,
            year: selectedYear,
        },
        onError: () => {
            showTimeClockError('loading', 'pay periods');
        },
    });
    const {
        data: pendingClockOutData,
        refetch: refetchPendingClockOutAction,
        loading: isLoadingPendingClockOut,
    } = useQuery(PayrollGeneralQuery.HAS_PENDING_CLOCK_OUT_ACTION, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        onError: () => {
            showTimeClockError('loading', 'latest clock action');
        },
    });
    const dailyLogRequestSeqRef = useRef(0);
    const [addDailyActionLog, { loading: isAddingDailyActionLog }] = useMutation(
        PayrollGeneralMutation.ADD_DAILY_ACTION_LOG,
    );
    const [printTimeSheet, { loading: isPrintingTimeSheet }] = useMutation(HoursWorkedMutation.PRINT_TIME_SHEET, {
        onCompleted: (mutationData) => {
            if (mutationData?.printPayrollTimeSheet) {
                printJS({
                    type: 'pdf',
                    showModal: false,
                    printable: mutationData.printPayrollTimeSheet,
                });
            }
        },
        onError: (error) => ModalUtils.errorMessage(null, error),
    });
    const isAnyMutationLoading = isAddingDailyActionLog || isPrintingTimeSheet;
    const isActionButtonsDisabled = isAnyMutationLoading || executingLogOut || isLoadingPayPeriods
                                    || isLoadingPendingClockOut || isDailyActionLogLoading || isFetchingMoreDailyLogs;
    const payPeriods = payPeriodsData?.getPayPeriods;
    const reportPeriodOptions = useMemo(() => payPeriods || [], [payPeriods]);
    const availableYears = Array.from({ length: 3 }, (_, i) => currentYear - i);
    const patchState = (payload) => dispatch({
        type: 'PATCH_STATE',
        payload,
    });
    const handleFieldChange = (field) => (event) => patchState({ [field]: event.target.value });

    useEffect(() => {
        refetch({
            lotName: selectedLotName,
            year: selectedYear,
        }).catch(() => null);
    }, [selectedYear, refetch, selectedLotName]);

    useEffect(() => {
        const currentPeriod = reportPeriodOptions.find((p) => p.includes('(Current)'));
        const defaultPeriod = currentPeriod || reportPeriodOptions[0] || '';
        patchState({ selectedPeriod: defaultPeriod });
    }, [reportPeriodOptions]);

    useEffect(() => {
        appliedLogSearchTermRef.current = appliedLogSearchTerm;
    }, [appliedLogSearchTerm]);

    useEffect(() => {
        let isMounted = true;
        const requestId = dailyLogRequestSeqRef.current + 1;
        dailyLogRequestSeqRef.current = requestId;

        const loadInitialDailyLogs = async () => {
            dispatch({
                type: 'PATCH_STATE',
                payload: {
                    isDailyActionLogLoading: true,
                    hasMoreDailyLogs: true,
                    dailyActionLogRows: [],
                },
            });

            try {
                const queryResult = await apolloClient.query({
                    query: PayrollGeneralQuery.GET_DAILY_ACTION_LOG,
                    fetchPolicy: FetchPolicy.NETWORK_ONLY,
                    variables: {
                        offset: 0,
                        limit: DAILY_LOG_PAGE_SIZE,
                        search: isSupervisor ? appliedLogSearchTerm || null : null,
                    },
                });

                if (!isMounted || requestId !== dailyLogRequestSeqRef.current) {
                    return;
                }

                const loadedItems = queryResult?.data?.getDailyActionLog || [];
                dispatch({
                    type: 'PATCH_STATE',
                    payload: {
                        dailyActionLogRows: loadedItems,
                        hasMoreDailyLogs: loadedItems.length >= DAILY_LOG_PAGE_SIZE,
                    },
                });
            } catch {
                if (isMounted && requestId === dailyLogRequestSeqRef.current) {
                    dispatch({
                        type: 'PATCH_STATE',
                        payload: {
                            dailyActionLogRows: [],
                            hasMoreDailyLogs: false,
                        },
                    });
                    showTimeClockError('loading', 'daily action log');
                }
            } finally {
                if (isMounted && requestId === dailyLogRequestSeqRef.current) {
                    dispatch({
                        type: 'PATCH_STATE',
                        payload: { isDailyActionLogLoading: false },
                    });
                }
            }
        };

        loadInitialDailyLogs();

        return () => {
            isMounted = false;
        };
    }, [apolloClient, appliedLogSearchTerm, isSupervisor]);

    const handleLogSearchKeyDown = (event) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        patchState({ appliedLogSearchTerm: logSearchTerm.trim() });
    };

    const formatDateTimeForInput = (dateTime) => moment
        .tz(dateTime, userTimeZone)
        .format(INPUT_DATE_TIME_FORMAT);

    const calculateReturnTimestamp = (minutesFromNow) => {
        const now = moment.tz(userTimeZone);
        const returnTime = now.clone().add(minutesFromNow, 'minutes');
        return returnTime.utc().toISOString();
    };

    const calculateMedicalReturnTimestamp = () => {
        if (!medicalReturnDateTime) {
            return '';
        }

        return moment.tz(medicalReturnDateTime, 'YYYY-MM-DDTHH:mm', userTimeZone).utc().toISOString();
    };

    const getCurrentDateTimeForInput = () => formatDateTimeForInput(new Date());

    const buildActionReturnDialogPayload = (reason) => {
        const dialogPayloadByReason = {
            [TIME_CLOCK_ACTION_REASON.STOP_FOR_SHORT_BREAK]: {},
            [TIME_CLOCK_ACTION_REASON.STOP_FOR_MEDICAL]: {
                medicalReturnDateTime: getCurrentDateTimeForInput(),
            },
            [TIME_CLOCK_ACTION_REASON.STOP_FOR_OTHER]: {
                medicalReturnDateTime: getCurrentDateTimeForInput(),
            },
            [TIME_CLOCK_ACTION_REASON.STOP_FOR_VACATION]: {
                selectedVacationReason: VACATION_REASON_OPTIONS[0],
                medicalReturnDateTime: getCurrentDateTimeForInput(),
            },
        };

        return dialogPayloadByReason[reason];
    };

    const closeActionReturnDialog = () => {
        dispatch({ type: 'RESET_ACTION_DIALOG' });
    };

    const reloadDailyActionLog = useCallback(async () => {
        const requestId = dailyLogRequestSeqRef.current + 1;
        dailyLogRequestSeqRef.current = requestId;
        isFetchingMoreDailyLogsRef.current = false;

        dispatch({
            type: 'PATCH_STATE',
            payload: {
                isDailyActionLogLoading: true,
                isFetchingMoreDailyLogs: false,
                hasMoreDailyLogs: true,
                dailyActionLogRows: [],
            },
        });

        try {
            const [queryResult] = await Promise.all([
                apolloClient.query({
                    query: PayrollGeneralQuery.GET_DAILY_ACTION_LOG,
                    fetchPolicy: FetchPolicy.NETWORK_ONLY,
                    variables: {
                        offset: 0,
                        limit: DAILY_LOG_PAGE_SIZE,
                        search: isSupervisor ? appliedLogSearchTermRef.current || null : null,
                    },
                }),
                refetchPendingClockOutAction().catch(() => null),
            ]);

            if (requestId !== dailyLogRequestSeqRef.current) {
                return;
            }

            const loadedItems = queryResult?.data?.getDailyActionLog || [];
            dispatch({
                type: 'PATCH_STATE',
                payload: {
                    dailyActionLogRows: loadedItems,
                    hasMoreDailyLogs: loadedItems.length >= DAILY_LOG_PAGE_SIZE,
                },
            });
        } catch {
            if (requestId === dailyLogRequestSeqRef.current) {
                dispatch({
                    type: 'PATCH_STATE',
                    payload: {
                        dailyActionLogRows: [],
                        hasMoreDailyLogs: false,
                    },
                });
                showTimeClockError('loading', 'daily action log');
            }
        } finally {
            if (requestId === dailyLogRequestSeqRef.current) {
                dispatch({
                    type: 'PATCH_STATE',
                    payload: { isDailyActionLogLoading: false },
                });
            }
        }
    }, [apolloClient, isSupervisor, refetchPendingClockOutAction]);

    const openLogoutConfirmDialog = () => {
        patchState({ isLogoutConfirmDialogOpen: true });
    };

    const closeLogoutConfirmDialog = () => {
        patchState({ isLogoutConfirmDialogOpen: false });
    };

    const handleLogoutConfirmYes = async () => {
        closeLogoutConfirmDialog();
        await logOut();
    };

    const handleLogoutConfirmNo = () => {
        closeLogoutConfirmDialog();
        reloadDailyActionLog().catch(() => null);
    };

    const handleAction = async (reason) => {
        if (isActionButtonsDisabled) {
            return;
        }

        const actionReturnDialogPayload = buildActionReturnDialogPayload(reason);

        if (actionReturnDialogPayload) {
            patchState({
                ...actionReturnDialogPayload,
                actionReturnDialogReason: reason,
            });
            return;
        }

        try {
            const response = await addDailyActionLog({
                variables: {
                    input: {
                        reason,
                    },
                },
            });

            if (response?.data?.addDailyActionLog) {
                openLogoutConfirmDialog();
            }
        } catch {
            showTimeClockError('save', 'Time Clock entry');
        }
    };

    const handleConfirmActionReturn = async () => {
        if (isActionButtonsDisabled) {
            return;
        }

        const isShortBreakAction = actionReturnDialogReason === TIME_CLOCK_ACTION_REASON.STOP_FOR_SHORT_BREAK;
        const isOtherAction = actionReturnDialogReason === TIME_CLOCK_ACTION_REASON.STOP_FOR_OTHER;
        const isVacationAction = actionReturnDialogReason === TIME_CLOCK_ACTION_REASON.STOP_FOR_VACATION;
        const returnTimestamp = isShortBreakAction
            ? calculateReturnTimestamp(Number(selectedReturnTime))
            : calculateMedicalReturnTimestamp();

        if (!returnTimestamp) {
            ModalUtils.errorMessage(null, UI_TEXT.RETURN_TIME_REQUIRED);
            return;
        }

        if (isOtherAction && !otherManualReason.trim()) {
            ModalUtils.errorMessage(null, UI_TEXT.REASON_REQUIRED);
            return;
        }

        try {
            const actionReason = actionReturnDialogReason;
            let reasonToSend = actionReason;

            if (isOtherAction) {
                reasonToSend = otherManualReason.trim();
            } else if (isVacationAction) {
                reasonToSend = selectedVacationReason;
            }

            closeActionReturnDialog();

            const response = await addDailyActionLog({
                variables: {
                    input: {
                        reason: reasonToSend,
                        returnTimestamp,
                    },
                },
            });

            if (response?.data?.addDailyActionLog) {
                openLogoutConfirmDialog();
            }
        } catch {
            showTimeClockError('save', 'Time Clock entry');
        }
    };

    const onGenerateReport = () => {
        if (isActionButtonsDisabled) {
            return;
        }

        const payPeriod = (selectedPeriod || '').replace(' (Current)', '').trim();
        const employeeId = Number(userInformation?.userId || 0);
        const lotId = new KeyStore().getSelectedLot()?.lotId || 0;

        if (!payPeriod || employeeId <= 0) {
            showTimeClockError('generate', 'Time Sheet report');
            return;
        }
        printTimeSheet({
            variables: {
                payPeriod,
                employeeId,
                lotId,
            },
        }).catch(() => null);
    };

    const handleLoadMoreDailyLogs = async () => {
        if (isDailyActionLogLoading || isFetchingMoreDailyLogsRef.current || !hasMoreDailyLogs) {
            return;
        }

        const currentLogs = dailyActionLogRows;

        if (!currentLogs.length) {
            return;
        }

        const requestId = dailyLogRequestSeqRef.current + 1;
        dailyLogRequestSeqRef.current = requestId;
        const requestSearchTerm = appliedLogSearchTermRef.current;

        isFetchingMoreDailyLogsRef.current = true;
        dispatch({
            type: 'PATCH_STATE',
            payload: { isFetchingMoreDailyLogs: true },
        });

        try {
            const pagedResponse = await apolloClient.query({
                query: PayrollGeneralQuery.GET_DAILY_ACTION_LOG,
                fetchPolicy: FetchPolicy.NETWORK_ONLY,
                variables: {
                    offset: currentLogs.length,
                    limit: DAILY_LOG_PAGE_SIZE,
                    search: isSupervisor ? requestSearchTerm || null : null,
                },
            });

            if (
                requestId !== dailyLogRequestSeqRef.current
                || requestSearchTerm !== appliedLogSearchTermRef.current
            ) {
                return;
            }

            const nextBatch = pagedResponse?.data?.getDailyActionLog || [];
            const previousIds = new Set(currentLogs.map((item) => item.timeId));
            const uniqueNextBatch = nextBatch.filter((item) => !previousIds.has(item.timeId));

            dispatch({
                type: 'PATCH_STATE',
                payload: {
                    dailyActionLogRows: [...currentLogs, ...uniqueNextBatch],
                    hasMoreDailyLogs: nextBatch.length >= DAILY_LOG_PAGE_SIZE,
                },
            });
        } catch {
            showTimeClockError('loading', 'daily action log');
        } finally {
            isFetchingMoreDailyLogsRef.current = false;
            dispatch({
                type: 'PATCH_STATE',
                payload: { isFetchingMoreDailyLogs: false },
            });
        }
    };

    const handleLoadMoreRef = useRef(handleLoadMoreDailyLogs);
    useEffect(() => {
        handleLoadMoreRef.current = handleLoadMoreDailyLogs;
    });

    const throttledLoadMore = useMemo(() => throttle(() => {
        handleLoadMoreRef.current();
    }, 200), []);

    const handleDailyLogScroll = (event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isNearBottom = scrollHeight - scrollTop - clientHeight <= DAILY_LOG_SCROLL_THRESHOLD;

        if (isNearBottom) {
            throttledLoadMore();
        }
    };

    const dailyLogItems = useMemo(() => {
        const currentDateInUserTimeZone = moment().tz(userTimeZone);
        const getLogDisplayTime = (utcDate) => {
            const logDateInUserTimeZone = moment.utc(utcDate).tz(userTimeZone);
            const logTimeFormat = logDateInUserTimeZone.isSame(currentDateInUserTimeZone, 'day')
                ? DAILY_LOG_TIME_FORMAT
                : DAILY_LOG_DATE_TIME_FORMAT;

            return logDateInUserTimeZone.format(logTimeFormat);
        };

        return (dailyActionLogRows || [])
            .flatMap((item) => {
                const firstName = (item.firstName || '').trim();
                const lastName = (item.lastName || '').trim();
                const fullName = `${firstName} ${lastName}`.trim();
                const baseUserInfo = {
                    userId: item.userId,
                    firstName,
                    lastName,
                    fullName,
                    userName: fullName || item.userName || '-',
                };
                const entries = [];

                if (item.timeInUtc) {
                    entries.push({
                        ...baseUserInfo,
                        icon: PlayArrowIcon,
                        timeId: `${item.timeId}-in`,
                        reasonValue: item.timeInReason || '',
                        action: (((item.timeInReason || '').toString()).toLowerCase().includes('start')
                            ? (item.timeInReason || '').toString()
                            : `Start ${(item.timeInReason || '').toString()}`).trim(),
                        time: getLogDisplayTime(item.timeInUtc),
                        active: true,
                        timestamp: new Date(item.timeInUtc).getTime(),
                    });
                }

                if (item.timeOutUtc) {
                    entries.push({
                        ...baseUserInfo,
                        icon: StopIcon,
                        timeId: `${item.timeId}-out`,
                        reasonValue: item.timeOutReason || '',
                        action: (((item.timeOutReason || '').toString()).toLowerCase().includes('end day')
                            ? (item.timeOutReason || '').toString()
                            : `Stop for ${(item.timeOutReason || '').toString()}`).trim(),
                        time: getLogDisplayTime(item.timeOutUtc),
                        active: false,
                        timestamp: new Date(item.timeOutUtc).getTime(),
                    });
                }

                return entries;
            })
            .sort((a, b) => b.timestamp - a.timestamp);
    }, [dailyActionLogRows, userTimeZone]);

    const currentUserId = `${userInformation?.userId || ''}`;
    const currentUserDailyLogItems = dailyLogItems.filter((item) => `${item.userId || ''}` === currentUserId);
    const latestCurrentUserAction = currentUserDailyLogItems[0];
    const hasPendingClockOutAction = pendingClockOutData?.hasPendingClockOutAction;

    const isClockActive = latestCurrentUserAction
        ? Boolean(latestCurrentUserAction.active)
        : Boolean(hasPendingClockOutAction);
    const latestReason = (latestCurrentUserAction?.reasonValue || '')
        .toString()
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase();
    const isLeaveForTheDay = (
        latestReason === TIME_CLOCK_ACTION_REASON.LEAVE_FOR_THE_DAY.toLowerCase().replace(/_/g, ' ')
        && currentUserId !== ''
    );

    return (
        <Box className={classes.root}>
            <Box className={classes.container}>
                <Box className={classes.mainContent}>
                    {/* Header Card */}
                    <Paper className={classes.headerCard}>
                        <Box className={`${classes.statusBadge} ${isClockActive ? classes.statusBadgeActive : ''}`}>
                            <Box className={`${classes.statusDot} ${isClockActive ? classes.statusDotActive : ''}`} />
                            <span>{isClockActive ? UI_TEXT.SHIFT_ACTIVE : UI_TEXT.CURRENTLY_OUT}</span>
                        </Box>
                        <Typography className={classes.clock}>
                            <ClockDisplay userTimeZone={userTimeZone} timeFormat={isMobile ? TIME_FORMAT.MOBILE : TIME_FORMAT.DESKTOP} />
                        </Typography>
                        {!isLeaveForTheDay && !isActionButtonsDisabled && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                className={`${classes.startButton} ${isClockActive ? classes.stopButton : ''}`}
                                startIcon={isClockActive ? <StopIcon /> : <PlayCircleIcon />}
                                onClick={() => handleAction(
                                    isClockActive
                                        ? TIME_CLOCK_ACTION_REASON.LEAVE_FOR_THE_DAY
                                        : TIME_CLOCK_ACTION_REASON.START_WORK,
                                )}
                            >
                                {isClockActive ? UI_TEXT.LEAVE_FOR_THE_DAY : UI_TEXT.START_WORK}
                            </Button>
                        )}
                        {isLeaveForTheDay && (
                            <Box className={classes.leaveForDayNotice}>
                                <InfoOutlinedIcon className={classes.leaveForDayIcon} />
                                <Typography className={classes.leaveForDayText}>
                                    You already clocked out Leaving for the day. Please contact your administrator to adjust the time.
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* Action Return Time Dialog */}
                    <Dialog open={Boolean(actionReturnDialogReason)} onClose={closeActionReturnDialog} maxWidth="sm" fullWidth>
                        <DialogTitle>{UI_TEXT.RETURN_DIALOG_TITLE}</DialogTitle>
                        <DialogContent>
                            <Box pt={2}>
                                {actionReturnDialogReason === TIME_CLOCK_ACTION_REASON.STOP_FOR_SHORT_BREAK && (
                                    <>
                                        <Typography variant="body2" className={classes.dialogSectionTitle}>
                                            {UI_TEXT.SELECT_RETURN_TIME}
                                        </Typography>
                                        <Select
                                            value={selectedReturnTime}
                                            onChange={handleFieldChange('selectedReturnTime')}
                                            fullWidth
                                        >
                                            {BREAK_RETURN_TIME_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </>
                                )}
                                {actionReturnDialogReason === TIME_CLOCK_ACTION_REASON.STOP_FOR_MEDICAL && (
                                    <>
                                        <Typography variant="body2" className={classes.dialogSectionTitle}>
                                            {UI_TEXT.SELECT_DATE_TIME}
                                        </Typography>
                                        <TextField
                                            type="datetime-local"
                                            value={medicalReturnDateTime}
                                            onChange={handleFieldChange('medicalReturnDateTime')}
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                    </>
                                )}
                                {actionReturnDialogReason === TIME_CLOCK_ACTION_REASON.STOP_FOR_OTHER && (
                                    <>
                                        <Typography variant="body2" className={classes.dialogSectionTitle}>
                                            {UI_TEXT.SELECT_DATE_TIME}
                                        </Typography>
                                        <TextField
                                            type="datetime-local"
                                            value={medicalReturnDateTime}
                                            onChange={handleFieldChange('medicalReturnDateTime')}
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                        <TextField
                                            value={otherManualReason}
                                            onChange={handleFieldChange('otherManualReason')}
                                            label="Reason"
                                            placeholder="Enter reason"
                                            fullWidth
                                            inputProps={{ maxLength: 50 }}
                                            className={classes.dialogReasonField}
                                        />
                                    </>
                                )}
                                {actionReturnDialogReason === TIME_CLOCK_ACTION_REASON.STOP_FOR_VACATION && (
                                    <>
                                        <Typography variant="body2" className={classes.dialogSectionTitle}>
                                            {UI_TEXT.SELECT_REASON}
                                        </Typography>
                                        <Select
                                            value={selectedVacationReason}
                                            onChange={handleFieldChange('selectedVacationReason')}
                                            fullWidth
                                        >
                                            {VACATION_REASON_OPTIONS.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Typography variant="body2" className={classes.dialogSectionTitleSpaced}>
                                            {UI_TEXT.SELECT_DATE_TIME}
                                        </Typography>
                                        <TextField
                                            type="datetime-local"
                                            value={medicalReturnDateTime}
                                            onChange={handleFieldChange('medicalReturnDateTime')}
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                    </>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            {!isActionButtonsDisabled && (
                                <>
                                    <Button onClick={closeActionReturnDialog} color="default">
                                        {UI_TEXT.CANCEL}
                                    </Button>
                                    <Button onClick={handleConfirmActionReturn} color="primary" variant="contained">
                                        {UI_TEXT.CONFIRM}
                                    </Button>
                                </>
                            )}
                        </DialogActions>
                    </Dialog>

                    <ConfirmDialog
                        open={isLogoutConfirmDialogOpen}
                        title={UI_TEXT.LOGOUT_CONFIRM_TITLE}
                        description={UI_TEXT.LOGOUT_CONFIRM_DESCRIPTION}
                        titlePrimary={UI_TEXT.YES}
                        titleSecondary={UI_TEXT.NO}
                        onClickPrimary={handleLogoutConfirmYes}
                        onClickSecondary={handleLogoutConfirmNo}
                        onClose={handleLogoutConfirmNo}
                    />

                    {/* Dashboard Grid */}
                    <Box className={classes.dashboardGrid}>
                        {/* Left Section: Actions & Log */}
                        <Box className={classes.leftColumn}>
                            {/* Secondary Actions Grid */}
                            {isClockActive && !isLeaveForTheDay && !isActionButtonsDisabled && (
                                <Box className={classes.actionGrid}>
                                    {ACTION_TILES.map((action, idx) => {
                                        const IconComponent = action.icon;
                                        const actionVariantClass = ACTION_TILE_COLOR_CLASS_BY_REASON[action.reason];
                                        return (
                                            <Box
                                                key={idx}
                                                className={`${classes.actionButton} ${classes[actionVariantClass] || ''}`}
                                                onClick={() => handleAction(action.reason)}
                                            >
                                                <IconComponent className={classes.actionIcon} />
                                                <Typography className={classes.actionLabel}>
                                                    {action.label}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            {/* Action Log Section */}
                            <Paper className={classes.logCard}>
                                <Box className={classes.logHeader}>
                                    <Typography className={classes.logTitle}>
                                        Daily Action Log
                                    </Typography>
                                    {isSupervisor && (
                                        <Box className={classes.logSearchWrapper}>
                                            <SearchIcon className={classes.logSearchIcon} />
                                            <TextField
                                                value={logSearchTerm}
                                                onChange={handleFieldChange('logSearchTerm')}
                                                onKeyDown={handleLogSearchKeyDown}
                                                placeholder={UI_TEXT.SEARCH_PLACEHOLDER}
                                                variant="outlined"
                                                className={classes.logSearchInput}
                                                fullWidth
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <Box className={classes.logContent} onScroll={handleDailyLogScroll}>
                                    {isDailyActionLogLoading && (
                                        <Box className={classes.logItem}>
                                            <Typography className={classes.logTime}>
                                                {UI_TEXT.LOADING_DAILY_LOG}
                                            </Typography>
                                        </Box>
                                    )}
                                    {!isDailyActionLogLoading && dailyLogItems.length === 0 && (
                                        <Box className={classes.logItem}>
                                            <Typography className={classes.logTime}>
                                                {UI_TEXT.NO_DAILY_ACTIONS}
                                            </Typography>
                                        </Box>
                                    )}
                                    {!isDailyActionLogLoading && dailyLogItems.map((item) => {
                                        const IconComponent = item.icon;
                                        return (
                                            <Box
                                                key={item.timeId}
                                                className={`${classes.logItem} ${
                                                    item.active
                                                        ? classes.logItemActive
                                                        : classes.logItemPast
                                                }`}
                                            >
                                                <Box className={classes.logItemContent}>
                                                    <Avatar
                                                        className={`${classes.logAvatar} ${
                                                            item.active
                                                                ? classes.logAvatarActive
                                                                : classes.logAvatarPast
                                                        }`}
                                                    >
                                                        <IconComponent
                                                            className={item.active ? classes.logIconActive : classes.logIconPast}
                                                        />
                                                    </Avatar>
                                                    <Box className={classes.logItemInner}>
                                                        <Box className={classes.logText}>
                                                            <Typography className={classes.logAction}>
                                                                {item.action}
                                                            </Typography>
                                                            <Typography className={classes.logUserName}>
                                                                {item.userName}
                                                            </Typography>
                                                        </Box>
                                                        <Box className={classes.logTimeWrapper}>
                                                            <Typography className={classes.logTime}>
                                                                {item.time}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                    {isFetchingMoreDailyLogs && (
                                        <Box className={classes.logItem}>
                                            <Typography className={classes.logTime}>
                                                {UI_TEXT.LOADING_MORE_ACTIONS}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </Box>

                        {/* Right Sidebar: Reports */}
                        <Paper className={classes.sidebar}>
                            <Box className={classes.sidebarHeader}>
                                <Typography className={classes.sidebarTitle}>
                                    Time Sheet Reports
                                </Typography>
                            </Box>
                            <Box className={classes.sidebarContent}>
                                <Box className={classes.yearFilterContainer}>
                                    <Typography className={classes.yearFilterLabel}>
                                        Filter by Year
                                    </Typography>
                                    <Select
                                        value={selectedYear}
                                        onChange={handleFieldChange('selectedYear')}
                                        className={classes.yearFilterSelect}
                                        fullWidth
                                    >
                                        {availableYears.map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                                {isMobile ? (
                                    <Select
                                        value={selectedPeriod}
                                        onChange={handleFieldChange('selectedPeriod')}
                                        className={classes.periodSelectMobile}
                                        fullWidth
                                        displayEmpty
                                    >
                                        {!selectedPeriod && (
                                            <MenuItem value="" disabled>
                                                Select period
                                            </MenuItem>
                                        )}
                                        {reportPeriodOptions.map((periodOption) => (
                                            <MenuItem key={periodOption} value={periodOption}>
                                                {periodOption}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                ) : (
                                    <Box className={classes.periodList}>
                                        {reportPeriodOptions.map((periodOption) => (
                                            <button
                                                key={periodOption}
                                                type="button"
                                                className={`${classes.periodOption} ${
                                                    selectedPeriod === periodOption
                                                        ? classes.periodOptionActive
                                                        : ''
                                                }`}
                                                onClick={() => patchState({ selectedPeriod: periodOption })}
                                            >
                                                {periodOption}
                                            </button>
                                        ))}
                                    </Box>
                                )}
                                {!isActionButtonsDisabled && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.generateButton}
                                        fullWidth
                                        startIcon={<PrintIcon />}
                                        onClick={onGenerateReport}
                                    >
                                        {UI_TEXT.GENERATE_REPORT}
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default TimeClockHome;
